/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService, FormStateBuilder } from '@gh/builder-shared';
import { DialogService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { filterTracker, RouteDataService, SlicedDataSource, toIsoDateTime } from '@gh/core-data';
import {
  AppRouter,
  DateTimeTypes,
  filterableForm,
  FilterableFormState,
  getRouteInitData,
  UserContext,
} from '@gh/common-shared';
import { ROW_DELETED_CLASS } from '@gh/common-internal-shared';
import { CareerEnquiriesBusinessService } from '../../resource/career-enquiries-business.service';
import { careerEnquiriesDetailsDialogRoute } from '../career-enquiries-details/career-enquiries-details.component';
import { filter$, switchMap$ } from '@gh/rx/operators';

@Component({
  selector: 'gh-career-enquiries-list',
  template: require('./career-enquiries-list.component.html'),
  styles: [require('./career-enquiries-list.component.scss')],
})
export class CareerEnquiriesListComponent implements OnInit {
  dataSource: SlicedDataSource<any>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  form: FilterableFormState<any>;

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private businessService: CareerEnquiriesBusinessService,
              private router: AppRouter,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private userContext: UserContext,
              private waitings: WaitingService,
              private formStateBuilder: FormStateBuilder) {
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        date: {
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
          sort: 'desc',
        },
        address: {
          valueFormatter: ({data}: { data: any }) => `${data.city || ''} ${data.state || ''} ${data.postcode || ''}`,
        },
      },
      {
        getRowNodeId: (data: any) => data.id,
        rowClassRules: {
          [ROW_DELETED_CLASS]: ({data}: { data: any }) => data.status === 'DELETED',
        },
      });
  }

  ngOnInit(): void {
    const {gridLayout} = getRouteInitData(this.route);

    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: {
        showDeleted: true,
        dateFrom: '',
        dateTo: '',
      },
      children: {
        searchString: void 0,
        showDeleted: void 0,
        dateFrom: void 0,
        dateTo: void 0,
      },
    }));

    const tracker = this.routeData
      .tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      const updatedFilter = {
        ...filter,
        // tslint:disable-next-line:no-null-keyword
        dateFrom: filter.dateFrom ? toIsoDateTime(new Date(filter.dateFrom)) : null,
        // tslint:disable-next-line:no-null-keyword
        dateTo: filter.dateTo ? toIsoDateTime(new Date(filter.dateTo)) : null,
      };
      this.dataSource = this.businessService.findAllAsDataSource(updatedFilter);
    });
  }

  goToAel(enquiry: any): void {
    this.navigationService.goToAel({
      label: 'lbl_career_enquiries',
      entityId: enquiry.id, // tslint:disable-line:no-non-null-assertion
      entityType: 'CAREER_ENQUIRY',
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onReset(): void {
    this.form.setValue({
      ...this.form.value,
      dateFrom: null,
      dateTo: null,
    });
    this.form.emitFilter();
  }

  onDeleteOrUndelete(enquiry: any, status: boolean): void {
    this.dialogService.confirmation(
      yesNoConfirmation(status
        ? 'msg_career_enquiry_delete'
        : 'msg_career_enquiry_undelete')).pipe(
      filter$(yesAnswer),
      switchMap$(() =>
                   this.businessService.onDeleteOrUndelete(enquiry.id, status).pipe(this.waitings.operator())))
      .subscribe((res) => {
        this.form.emitFilter();
      });
  }

  onOpenDetails(event: any): void {
    this.dialogService.openWithInput(careerEnquiriesDetailsDialogRoute, <any>{
      details: event.data,
    }).afterClosed();
  }
}
